import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Select from "@/components/Select";
import { lock } from "@/lib/perm";
import { useAuth } from "@/context/AuthContext";

const fmt = (v) => { if (!v) return "—"; const [y, m, d] = String(v).slice(0, 10).split("-"); return `${d}.${m}.${y}`; };
const STATE_STYLE = { "Activ": "badge-active", "Suspendat": "badge-submitted", "Detașat": "badge-submitted", "Încetat": "badge-inactive" };
const NEEDS_BASIS = ["suspendare", "incetare", "reactivare", "detasare"];
const EMPTY = { start_date: "", end_date: "", legal_basis: "", explanation: "", employer_cui: "", employer_name: "", employer_nationality: "România" };

/**
 * Evenimentele raportului de muncă cu temei legal (ReGES): suspendare, reluarea activității, detașare, încetare, reactivare.
 * `onApplied(patch)` primește câmpurile de status ale raportului după eveniment, ca formularul deschis să nu le suprascrie cu
 * valori vechi la salvare.
 */
export default function EmploymentEvents({ employmentId, readOnly, onApplied }) {
  const { has } = useAuth();
  const [nom, setNom] = useState(null);
  const [data, setData] = useState({ state: "", status: "", events: [] });
  const [form, setForm] = useState(null);      // { type, ...câmpuri }
  const [msg, setMsg] = useState({ text: "", type: "info" });

  const load = () => api.get(`/mdm/employments/${employmentId}/events`).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { api.get("/mdm/employments/events/nomenclatoare").then(r => setNom(r.data)).catch(() => {}); }, []);
  useEffect(() => { setForm(null); setMsg({ text: "" }); load(); }, [employmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = data.status;
  const actions = [
    { type: "suspendare", label: "Suspendă", show: status === "active" },
    { type: "detasare", label: "Detașează", show: status === "active" },
    { type: "reluare", label: "Reia activitatea", show: status === "suspended" },
    { type: "incetare", label: "Încetează", show: status === "active" || status === "suspended", perm: "employments.deactivate" },
    { type: "reactivare", label: "Reactivează", show: status === "terminated" },
  ].filter(a => a.show);
  const titleOf = (t) => nom?.types.find(x => x.value === t)?.label || t;
  const basisKey = { suspendare: "suspendare", incetare: "incetare", reactivare: "reactivare", detasare: "detasare" };

  const open = (type) => setForm({ type, ...EMPTY, start_date: new Date().toISOString().slice(0, 10), legal_basis: type === "reactivare" ? "Reintegrare" : "" });
  const submit = async () => {
    const body = { event_type: form.type, start_date: form.start_date || null, end_date: form.end_date || null, legal_basis: form.legal_basis,
                   explanation: form.explanation, employer_cui: form.employer_cui, employer_name: form.employer_name, employer_nationality: form.employer_nationality };
    try {
      const r = await api.post(`/mdm/employments/${employmentId}/events`, body);
      setForm(null); await load(); onApplied && onApplied(r.data.employment);
      setMsg({ text: r.data.scheduled ? "Salvat. Schimbarea de status este programată pentru data aleasă." : "Salvat.", type: "success" });
    } catch (e) { setMsg({ text: e?.response?.data?.detail || "Eroare", type: "error" }); }
  };

  return (
    <div data-testid="empl-events">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 13, color: "#475569" }}>Starea curentă ReGES:</span>
        <span className={"badge " + (STATE_STYLE[data.state] || "")} data-testid="ev-state">{data.state || "—"}</span>
        <span style={{ flex: 1 }} />
        {actions.map(a => (
          <button key={a.type} type="button" className="btn btn-secondary" style={{ padding: "4px 12px", fontSize: 12.5 }} data-testid={`ev-btn-${a.type}`}
            onClick={() => open(a.type)} {...lock(!readOnly && has(a.perm || "employments.update"))}>{a.label}</button>))}
      </div>
      {msg.text && <div style={{ fontSize: 12.5, margin: "0 0 10px", color: msg.type === "error" ? "#991B1B" : "#166534" }} data-testid="ev-msg">{msg.text}</div>}

      {form && nom && (
        <div className="card" style={{ padding: 14, marginBottom: 14 }} data-testid="ev-form">
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{titleOf(form.type)}</div>
          <div className="grid-2">
            <div><label className="label label-req">{form.type === "incetare" ? "Data încetării" : form.type === "detasare" ? "Început detașare" : "De la data"}</label>
              <input className="input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="ev-start" /></div>
            {["suspendare", "detasare"].includes(form.type) && (
              <div><label className={"label" + (form.type === "detasare" ? " label-req" : "")}>{form.type === "detasare" ? "Sfârșit detașare" : "Sfârșit suspendare (dacă se cunoaște)"}</label>
                <input className="input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="ev-end" /></div>)}
            {NEEDS_BASIS.includes(form.type) && (
              <div style={{ gridColumn: "1/-1" }}><label className="label label-req">Temei legal</label>
                <Select className="select" value={form.legal_basis} onChange={e => setForm({ ...form, legal_basis: e.target.value })} data-testid="ev-basis">
                  <option value="">— alege temeiul legal —</option>
                  {nom.legal_bases[basisKey[form.type]].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select></div>)}
            {form.type === "detasare" && (
              <>
                <div><label className="label label-req">CUI angajator</label>
                  <input className="input" value={form.employer_cui} onChange={e => setForm({ ...form, employer_cui: e.target.value })} data-testid="ev-cui" /></div>
                <div><label className="label label-req">Denumire angajator</label>
                  <input className="input" value={form.employer_name} onChange={e => setForm({ ...form, employer_name: e.target.value })} data-testid="ev-employer" /></div>
                <div><label className="label">Naționalitatea angajatorului</label>
                  <input className="input" value={form.employer_nationality} onChange={e => setForm({ ...form, employer_nationality: e.target.value })} data-testid="ev-nationality" /></div>
              </>)}
            <div style={{ gridColumn: "1/-1" }}><label className="label">Explicații (opțional)</label>
              <input className="input" maxLength={255} value={form.explanation} onChange={e => setForm({ ...form, explanation: e.target.value })} data-testid="ev-explanation" /></div>
          </div>
          <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 8 }}>
            {form.type === "incetare" && "Încetarea ultimului raport al unei persoane îi inactivează contul. "}
            O dată viitoare programează schimbarea; ea se aplică automat în ziua respectivă. Istoricul nu se modifică: o greșeală se corectează cu un eveniment nou.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setForm(null)}>Anulează</button>
            <button type="button" className="btn btn-primary" onClick={submit} data-testid="ev-save">Salvează evenimentul</button>
          </div>
        </div>)}

      <div style={{ overflowX: "auto" }}>
        <table className="tbl" data-testid="ev-history">
          <thead><tr><th>Eveniment</th><th>De la</th><th>Până la</th><th>Temei legal</th><th>Explicații</th></tr></thead>
          <tbody>
            {data.events.map(ev => (
              <tr key={ev.id} data-testid={`ev-row-${ev.event_type}`}>
                <td style={{ fontWeight: 600 }}>{ev.type_label}{ev.source === "status_change" && <span style={{ fontSize: 10, color: "#64748B", marginLeft: 6 }}>(schimbare de status)</span>}</td>
                <td className="tabular-nums">{fmt(ev.start_date)}</td>
                <td className="tabular-nums">{ev.ended_at ? fmt(ev.ended_at) : ev.end_date ? `${fmt(ev.end_date)} (planificat)` : "—"}</td>
                <td style={{ maxWidth: 360 }}>{ev.basis_label || "—"}{ev.employer_name ? ` · ${ev.employer_name} (CUI ${ev.employer_cui})` : ""}</td>
                <td>{ev.explanation || "—"}</td>
              </tr>))}
            {data.events.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", color: "#94A3B8", padding: 18 }}>Niciun eveniment înregistrat.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
